import {
  latLngToMercatorMeters,
  latLngToPixel,
  metersPerPixel,
  pixelToLatLng,
  type LatLng,
} from './GeoMath';
import type { MapFrame } from './MapFrame';

/**
 * The browsing view — where the map is looking, which is *not* the same thing as where the site is.
 *
 * ## Why this exists
 *
 * The first version had no such thing. The view was derived from the site frame — "the frame, times
 * 2.2" — and every gesture recomputed it, which produced three separate failures that all read to a
 * user as "the map is broken":
 *
 * 1. **Dragging a corner moved the map.** Widening the quad widened the view that was drawn from it,
 *    which rescaled the ground under the cursor mid-drag, which moved the target away from the
 *    pointer. The corner chased itself and could never reach where it was aimed.
 * 2. **The image blanked on every pointer move.** A derived view meant a new image URL meant a new
 *    `<img>` meant a fetch, dozens per second, each one a blank box until it arrived — and enough of
 *    them in a row that the tile service started refusing.
 * 3. **Panning moved the wrong distance.** Metres-per-pixel was computed from the view's *requested*
 *    width, but `createFrame` floors to an integer zoom and returns the extent it actually got, which
 *    is between 1× and 2× larger. So the map moved between 50 % and 100 % of the drag.
 *
 * The common cause is that browsing and framing were the same state. They are separated here: the
 * view has its own centre and its own zoom, and nothing the site quad does changes it. Panning and
 * zooming move the view. Dragging corners moves the frame. Neither touches the other.
 *
 * ## Fractional zoom, integer tiles
 *
 * {@link MapView.zoom} is fractional so a wheel gesture is continuous rather than a sequence of
 * doublings. Tiles only exist at integer zooms, so the layer is cut at `round(zoom)` and scaled by
 * `2 ^ (zoom − tileZoom)` — at most a 1.41× stretch either way, which is invisible on aerial imagery
 * and is what every slippy map does.
 *
 * Pure arithmetic. No DOM, no network.
 */

export interface MapView {
  center: LatLng;
  /** Fractional Web Mercator zoom. Tiles are cut at the nearest integer and scaled to fit. */
  zoom: number;
}

/** Size of the viewport in CSS pixels. */
export interface ViewportSize {
  width: number;
  height: number;
}

/** A position in the viewport, in CSS pixels from its top-left corner. */
export interface ScreenPoint {
  x: number;
  y: number;
}

/** Zoomed out far enough to find a country; in far enough that the imagery has run out first. */
export const MIN_VIEW_ZOOM = 3;
export const MAX_VIEW_ZOOM = 21;

/** Standard slippy-map tile edge, in pixels. Every provider here serves 256. */
export const TILE_SIZE = 256;

/** How much wider than the site frame the view opens. Enough to see the street and the neighbours. */
export const VIEW_MARGIN = 2.4;

/** Where a point on Earth lands in the viewport. */
export function viewToScreen(view: MapView, size: ViewportSize, point: LatLng): ScreenPoint {
  const at = latLngToPixel(point, view.zoom);
  const center = latLngToPixel(view.center, view.zoom);
  return {
    x: at.x - center.x + size.width / 2,
    y: at.y - center.y + size.height / 2,
  };
}

/**
 * The inverse: what is under a point in the viewport.
 *
 * This is the one function a corner drag needs, and it is exact at any element size — the pointer's
 * own coordinates go in and the coordinate under it comes out, with no image-space round trip and no
 * dependence on the shape being dragged.
 */
export function screenToView(view: MapView, size: ViewportSize, at: ScreenPoint): LatLng {
  const center = latLngToPixel(view.center, view.zoom);
  return pixelToLatLng(
    center.x + at.x - size.width / 2,
    center.y + at.y - size.height / 2,
    view.zoom,
  );
}

/** Ground metres per CSS pixel at the view's centre — what a readout and a scale bar want. */
export function viewMetersPerPixel(view: MapView): number {
  return metersPerPixel(view.center.lat, view.zoom);
}

/**
 * The view dragged by a pointer delta.
 *
 * Dragging the content right by `dx` moves the centre left by `dx`, and the arithmetic happens in
 * the view's own pixels — so a drag lands exactly where it was released regardless of zoom, latitude
 * or element size. That is the whole of the pan bug's fix.
 */
export function panView(view: MapView, dx: number, dy: number): MapView {
  const center = latLngToPixel(view.center, view.zoom);
  return { ...view, center: pixelToLatLng(center.x - dx, center.y - dy, view.zoom) };
}

/**
 * The view zoomed, keeping whatever is under `anchor` under `anchor`.
 *
 * Anchoring on the cursor rather than the centre is the difference between a map you can aim and one
 * you have to chase: zooming toward a corner of the plot walks it off screen if the centre is the
 * fixed point.
 */
export function zoomViewAt(
  view: MapView,
  size: ViewportSize,
  toZoom: number,
  anchor: ScreenPoint,
): MapView {
  const zoom = clamp(toZoom, MIN_VIEW_ZOOM, MAX_VIEW_ZOOM);
  if (zoom === view.zoom) return view;

  const under = screenToView(view, size, anchor);
  const at = latLngToPixel(under, zoom);
  return {
    zoom,
    center: pixelToLatLng(at.x - anchor.x + size.width / 2, at.y - anchor.y + size.height / 2, zoom),
  };
}

/**
 * The view that shows a frame with room around it.
 *
 * Fractional, so a 60 m plot in a 560 px box fills the same fraction of the box as a 90 m one — the
 * old integer-zoom snap made the site rectangle jump between a third and two thirds of the pane for
 * no reason the user could see.
 */
export function viewForFrame(frame: MapFrame, size: ViewportSize, margin = VIEW_MARGIN): MapView {
  const across = Math.max(frame.widthMeters, frame.lengthMeters) * margin;
  const box = Math.max(64, Math.min(size.width, size.height));
  return { center: frame.center, zoom: zoomForGroundSpan(frame.center.lat, across, box) };
}

/** The fractional zoom at which `meters` of ground spans `pixels` of screen. */
export function zoomForGroundSpan(lat: number, meters: number, pixels: number): number {
  const wanted = Math.max(1e-6, meters / Math.max(1, pixels));
  const atZoomZero = metersPerPixel(lat, 0);
  return clamp(Math.log2(atZoomZero / wanted), MIN_VIEW_ZOOM, MAX_VIEW_ZOOM);
}

/* ---------------------------------- The tile grid ---------------------------------- */

/** One tile, and where it goes. `key` is stable across layouts so the DOM node can be reused. */
export interface TileRef {
  key: string;
  z: number;
  x: number;
  y: number;
  /** Position and size in the viewport, in CSS pixels. */
  left: number;
  top: number;
  size: number;
}

/**
 * Every tile touching the viewport, with its screen box.
 *
 * A tile's identity is `z/x/y` and nothing else, which is what makes this cheap: the same tile keeps
 * the same `<img>` and the same browser cache entry across a whole gesture, so panning and zooming
 * cost layout rather than network. That is the difference between this and the single re-fetched
 * static image it replaces.
 *
 * `pad` fetches a ring of tiles beyond the edge so a drag reveals loaded ground rather than grey.
 */
export function visibleTiles(
  view: MapView,
  size: ViewportSize,
  maxZoom: number,
  pad = 1,
): TileRef[] {
  const tileZoom = clamp(Math.round(view.zoom), 0, Math.floor(maxZoom));
  const scale = 2 ** (view.zoom - tileZoom);
  const tileSize = TILE_SIZE * scale;
  const count = 2 ** tileZoom;

  // World pixel of the viewport's top-left, at the *view's* fractional zoom.
  const center = latLngToPixel(view.center, view.zoom);
  const originX = center.x - size.width / 2;
  const originY = center.y - size.height / 2;

  const firstX = Math.floor(originX / tileSize) - pad;
  const lastX = Math.floor((originX + size.width) / tileSize) + pad;
  const firstY = Math.floor(originY / tileSize) - pad;
  const lastY = Math.floor((originY + size.height) / tileSize) + pad;

  const tiles: TileRef[] = [];
  for (let y = firstY; y <= lastY; y += 1) {
    // Longitude wraps, latitude does not: a tile row above the north pole has no imagery to ask for.
    if (y < 0 || y >= count) continue;
    for (let x = firstX; x <= lastX; x += 1) {
      const wrapped = ((x % count) + count) % count;
      tiles.push({
        key: `${tileZoom}/${wrapped}/${y}`,
        z: tileZoom,
        x: wrapped,
        y,
        left: x * tileSize - originX,
        top: y * tileSize - originY,
        size: tileSize,
      });
    }
  }
  return tiles;
}

/**
 * Where a tile from *any* zoom level lands in the current view.
 *
 * Used to keep the previous level's tiles on screen, behind the new ones, until the new ones have
 * loaded. Without it, crossing a zoom rounding boundary flashes the whole map grey — which is the
 * one remaining way a tile layer can still look broken.
 */
export function tileScreenBox(
  view: MapView,
  size: ViewportSize,
  z: number,
  x: number,
  y: number,
): { left: number; top: number; size: number } {
  const scale = 2 ** (view.zoom - z);
  const tileSize = TILE_SIZE * scale;
  const center = latLngToPixel(view.center, view.zoom);
  return {
    left: x * tileSize - (center.x - size.width / 2),
    top: y * tileSize - (center.y - size.height / 2),
    size: tileSize,
  };
}

/**
 * A tile's bounds in EPSG:3857 metres — what a bbox endpoint wants when it has no tile service.
 *
 * Serving an image server one tile-shaped request at a time rather than one viewport-shaped one is
 * what lets a provider with no tile cache still browse smoothly: the requests are identical between
 * gestures, so the browser cache answers most of them.
 */
export function tileMercatorBounds(z: number, x: number, y: number): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} {
  const northWest = pixelToLatLng(x * TILE_SIZE, y * TILE_SIZE, z);
  const southEast = pixelToLatLng((x + 1) * TILE_SIZE, (y + 1) * TILE_SIZE, z);
  const min = latLngToMercatorMeters({ lat: southEast.lat, lng: northWest.lng });
  const max = latLngToMercatorMeters({ lat: northWest.lat, lng: southEast.lng });
  return { minX: min.x, minY: min.y, maxX: max.x, maxY: max.y };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
